import { FileCopyOutlined as FileCopyOutlinedIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
	Box,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { useExportInstallmentsExcel } from "../hooks/useExportInstallmentsExcel";
import { useInstallmentHandlers } from "../hooks/useInstallmentHandlers";
import { useInstallmentModals } from "../hooks/useInstallmentModals";
import { ModalType } from "../types/modalTexts";
import { IInstallmentTable } from "../types/types";
import { InstallmentCreditCardMenu } from "./Menus/InstallmentCreditCardMenu";
import { GenericModalDetails } from "./Modals/GenericModalDetails";
import { ModalSelectInstallments } from "./Modals/ModalSelectInstallments";

interface Props {
	rows: IInstallmentTable[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	dataIni: string;
	dataFim: string;
	onRefresh: () => void;
}

export const TableInstallmentCreditCard = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, dataIni, dataFim, onRefresh }: Props) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [hoveredName, setHoveredName] = useState<string | null>(null);
	const [hoveredDocument, setHoveredDocument] = useState<string | null>(null);
	const [selectModalType, setSelectModalType] = useState<ModalType | null>(null);
	const { modalType, selectedRow, openModal, closeModal } = useInstallmentModals();
	const [isLoading, setIsLoading] = useState(false);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: IInstallmentTable) => {
		setAnchorEl(event.currentTarget);
		openModal(null, row);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleCopy = (value: string) => {
		navigator.clipboard.writeText(value).catch(console.error);
	};

	const handleOpenModal = (type: ModalType | "details", row: IInstallmentTable) => {
		if (type === "details") {
			openModal("details", row);
		} else {
			openModal(null, row);
			setSelectModalType(type);
		}
	};

	const handleExportToCsv = (row: IInstallmentTable) => {
		handleExportCsv(row.clientId, row.name);
	};

	const { handleExportPdf } = useInstallmentHandlers(dataIni, dataFim, setIsLoading);

	const { handleExportCsv } = useExportInstallmentsExcel(dataIni, dataFim, "credit_card", setIsLoading);

	return (
		<>
			<TableContainer
				component={Paper}
				variant="elevation"
				sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
			>
				<Table size="small" stickyHeader aria-label="Tabela de Cartão de Crédito">
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							{["Data Criação", "Parceiro", "Valor Total", "Garantias", "Ações"].map((header, i) => (
								<TableCell key={i} align={i === 4 ? "right" : i === 3 ? "center" : "left"}>
									<Typography variant="button">{header}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={row.clientId}>
								<TableCell>{formatDateFnsRemoveTimezone(row.data_generate)}</TableCell>
								<TableCell>
									<Stack spacing={0.5}>
										<Typography fontWeight="bold" noWrap>
											{row.name}
											{hoveredName === row.clientId && (
												<Tooltip title="Copiar Nome">
													<IconButton sx={{ fontSize: 10, ml: 2 }} onClick={() => handleCopy(row.name)}>
														<FileCopyOutlinedIcon sx={{ fontSize: 15 }} />
													</IconButton>
												</Tooltip>
											)}
										</Typography>
										<Typography
											variant="body2"
											color="textSecondary"
											noWrap
											onMouseEnter={() => setHoveredDocument(row.clientId)}
											onMouseLeave={() => setHoveredDocument(null)}
										>
											{globalMaskDocument(row.document)}
											{hoveredDocument === row.clientId && (
												<Tooltip title="Copiar Documento">
													<IconButton sx={{ fontSize: 10, ml: 1 }} onClick={() => handleCopy(row.document)}>
														<FileCopyOutlinedIcon sx={{ fontSize: 15 }} />
													</IconButton>
												</Tooltip>
											)}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell>
									<Typography fontWeight={500} color="textSecondary">
										{globalFormatCurrencyBRL(row.sum_amount)}
									</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="body2" color="textSecondary">
										{row.count_amount}
									</Typography>
								</TableCell>
								<TableCell align="right">
									<IconButton onClick={(e) => handleMenuOpen(e, row)} aria-label={`Ações para ${row.name}`}>
										<MoreVertIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>

				<InstallmentCreditCardMenu
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={handleMenuClose}
					selectedRow={selectedRow}
					onOpenModal={handleOpenModal}
					onExportPdf={handleExportPdf}
					onExportCsv={handleExportToCsv}
					
				/>

				<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
					<TablePagination
						component="div"
						count={totalCount}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={(_, newPage) => onPageChange(newPage)}
						onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
						rowsPerPageOptions={[10, 25, 50]}
						labelRowsPerPage="Linhas por página:"
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
					/>
				</Box>
			</TableContainer>
			<LoadingComp isopen={isLoading} />

			{/* Modais */}
			{modalType === "details" && selectedRow && (
				<GenericModalDetails open onClose={closeModal} dataIni={dataIni} dataFim={dataFim} row={selectedRow} paymentMethod="credit_card" />
			)}

			{selectModalType && selectedRow && (
				<ModalSelectInstallments
					open
					onClose={() => setSelectModalType(null)}
					dataIni={dataIni}
					dataFim={dataFim}
					row={selectedRow}
					modalType={selectModalType}
					filterByPaymentMethod="credit_card"
					onSuccess={onRefresh}
				/>
			)}
		</>
	);
};
